import { Event } from "@/lib/types";
import { CalendarDays, Clock, MapPin, Users } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { WaitlistDialog } from "./WaitlistDialog";
import { getCurrencySymbol } from "@/lib/currencyUtils";

interface EventDetailsContentProps {
  event: Event;
}

export const EventDetailsContent = ({ event }: EventDetailsContentProps) => {
  const navigate = useNavigate();
  const currencySymbol = getCurrencySymbol(event.currency);
  
  
  // Parse the date without timezone conversion
  const rawDate = new Date(event.date);
  const formattedDate = `${rawDate.toLocaleString('en-US', { 
    weekday: 'short',
    day: 'numeric', 
    month: 'short', 
    year: 'numeric',
    timeZone: 'UTC' // Use UTC to prevent local timezone conversion
  })}`;

  // Check if event is more than 5 hours past
  const eventDate = new Date(event.date);
  const currentTime = new Date();
  const fiveHoursInMs = 5 * 60 * 60 * 1000; // 5 hours in milliseconds
  const isEventPast = currentTime.getTime() - eventDate.getTime() > fiveHoursInMs;

  // Check if event is sold out
  const isSoldOut = () => {
    const hasEarlyBird = event.early_bird_price && event.quantity_early_bird && event.quantity_early_bird > 0;
    const hasGeneralAdmission = event.price_general_admission && event.quantity_standing && event.quantity_standing > 0;
    const hasVip = event.price_vip && event.quantity_vip && event.quantity_vip > 0;
    
    return !hasEarlyBird && !hasGeneralAdmission && !hasVip;
  };

  // Calculate the cheapest ticket price including Slay fee
  const calculateTotalPrice = () => {
    const prices = [];
    
    // Add available ticket prices
    if (event.early_bird_price && event.quantity_early_bird && event.quantity_early_bird > 0) {
      prices.push(event.early_bird_price);
    }
    if (event.price_general_admission && event.quantity_standing && event.quantity_standing > 0) {
      prices.push(event.price_general_admission);
    }
    if (event.price_vip && event.quantity_vip && event.quantity_vip > 0) {
      prices.push(event.price_vip);
    }
    
    // Find the cheapest price
    const cheapestPrice = Math.min(...prices);
    
    // Calculate Slay fee
    const slayFeePercentage = event.slay_fee_percentage || 0;
    const slayFeeFixed = event.slay_fee_fixed || 0;
    const slayFeeAmount = (cheapestPrice * slayFeePercentage / 100) + slayFeeFixed;
    
    // Return total price including Slay fee
    return cheapestPrice + slayFeeAmount;
  };

  const totalPrice = calculateTotalPrice();
  const soldOut = isSoldOut();

  const handleGetTicketsClick = () => {
    navigate(`/events/${event.seo_url}/ticketing`);
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const renderButton = () => {
    // Don't show waitlist for events that have passed more than 5 hours ago
    if (soldOut && !isEventPast) {
      return (
        <WaitlistDialog eventTitle={event.title}>
          <Button 
            className="w-full md:w-auto bg-purple-600 hover:bg-green-500 transition-colors duration-300 text-xl py-4 px-8 text-white"
          >
            Join Waitlist
          </Button>
        </WaitlistDialog>
      );
    }

    return (
      <Button 
        onClick={handleGetTicketsClick}
        disabled={isEventPast}
        className={`w-full md:w-auto ${
          isEventPast 
            ? 'bg-gray-400 cursor-not-allowed hover:bg-gray-400' 
            : 'bg-purple-600 hover:bg-green-500'
        } transition-colors duration-300 text-xl py-4 px-8 text-white`}
      >
        {isEventPast ? "You Missed It 👀" : "Get Tickets"}
      </Button>
    );
  };

  return (
    <div className="bg-white rounded-b-lg p-8 space-y-8">
      <div className="prose prose-lg max-w-none text-black">
        <h2 className="text-3xl font-semibold mb-6 text-[#9b87f5]">About This Event</h2>
        <p className="text-xl mb-6 whitespace-pre-wrap">{event.description}</p>

        {event.featured_artists && event.featured_artists.length > 0 && (
          <div className="mt-8">
            <h3 className="text-2xl font-semibold mb-4">Featuring:</h3>
            <ul className="space-y-4 pl-4">
              {event.featured_artists.map((artist, index) => (
                <li key={index} className="flex items-start space-x-3 text-xl">
                  <span className="text-yellow-400">⭐</span>
                  <span>
                    <strong>{artist.name}</strong> – {artist.description}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-8">
          <div className="space-y-4">
            <div className="flex items-center space-x-4 text-black">
              <CalendarDays className="h-6 w-6 flex-shrink-0" />
              <div>
                <p className="font-semibold text-xl">Date</p>
                <p className="text-lg">{formattedDate}</p>
                {event.date_extra_note && (
                  <p className="text-sm text-gray-600 italic mt-1">{event.date_extra_note}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-4 text-black">
              <Clock className="h-6 w-6 flex-shrink-0" />
              <div>
                <p className="font-semibold text-xl">Time</p>
                <p className="text-lg">{event.event_time_range}</p>
                {event.time_extra_note && (
                  <p className="text-sm text-gray-600 italic mt-1">{event.time_extra_note}</p>
                )}
              </div>
            </div>

            <div className="flex items-start space-x-4 text-black">
              <MapPin className="h-6 w-6 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-xl">Location</p>
                {event.location_url ? (
                  <a 
                    href={event.location_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg text-purple-600 hover:text-purple-800"
                  >
                    {event.location}
                  </a>
                ) : (
                  <p className="text-lg">{event.location}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {event.producer_name && (
            <div className="space-y-2 text-black">
              <p className="font-semibold text-xl flex items-center gap-2">
                <span>👥</span> Producer
              </p>
              <div className="ml-8">
                {event.producer_url ? (
                  <a 
                    href={event.producer_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg text-purple-600 hover:text-purple-800"
                  >
                    {event.producer_name}
                  </a>
                ) : (
                  <p className="text-lg">{event.producer_name}</p>
                )}
              </div>
            </div>
          )}

          {event.refund_policy && (
            <div className="space-y-2 text-black">
              <p className="font-semibold text-xl flex items-center gap-2">
                <span>💰</span> Refund Policy
              </p>
              <div className="ml-8">
                <p className="text-lg">{event.refund_policy}</p>
              </div>
            </div>
          )}

          {event.predicted_fans_range && event.predicted_fans_range !== "0" && (
            <div className="flex items-center space-x-4 text-black">
              <Users className="h-6 w-6" />
              <div>
                <p className="font-semibold text-xl">Predicted Fans Attending</p>
                <p className="text-lg">{event.predicted_fans_range}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="space-y-1">
            {/* Only show sold out message for active events, not past events */}
            {soldOut && !isEventPast ? (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-center">
                <p className="text-red-400 font-bold text-xl flex items-center justify-center gap-2">
                  <span className="text-2xl">🔥</span>
                  All Tickets Snatched. Join the Waitlist.
                </p>
              </div>
            ) : !isEventPast ? (
              <>
                <p className="text-xl text-black">From (inc. fees)</p>
                <div className="flex items-baseline space-x-2">
                  <p className="text-3xl font-bold text-black">
                    {currencySymbol}{totalPrice.toFixed(2)}
                  </p>
                </div>
              </>
            ) : null}
          </div>
          {renderButton()}
        </div>
      </div>
    </div>
  );
};
